import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Loader2, Calendar as CalendarIcon, Square, Circle } from 'lucide-react';
import { getFirstImageCandidate } from '../../api/iframeConfig.api';
import { getLatestImage } from '../../api/lastImage.api';
import { getRotationDegrees } from '../../../utils/cameraOrientation.js';
import { useOrientedMedia } from '../../../utils/useOrientedMedia.js';
import useSyncedZoom, { ZoomIndicator } from './useSyncedZoom';
import { DatePickerModal, formatDateEs, parseDateSafe, useDailyImagePicker } from './FirstLastImageCompare';
import { useFramingCheck, alignmentTransform, ahoraPrecalculado } from './frameAlign';
import FramingNotice, { FramingLoading } from './FramingNotice';

// "Rayos X": se ve la foto de AHORA completa y una lente (rectángulo o círculo)
// deja ver la foto de ANTES en el mismo lugar. Las dos fotos son de la misma
// cámara, así que quedan alineadas píxel a píxel dentro de un único stage que
// comparte el zoom del comparador.
//
// La lente se guarda en coordenadas del stage normalizadas (centro en 0..1 del
// ancho/alto, tamaño como fracción del ancho) y se dibuja de forma imperativa
// en requestAnimationFrame: arrastrarla no re-renderiza React.

const MIN_LENS_PX = 44;

const formatShort = (dateStr) => {
  const d = parseDateSafe(dateStr);
  return d ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}` : '';
};

const RayosXCompare = ({ user }) => {
  const feedId = user?.feedID;
  const rotationDeg = getRotationDegrees(user?.orientation);
  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [imagesReady, setImagesReady] = useState(false);
  const [shape, setShape] = useState('rect');
  const [opacity, setOpacity] = useState(100);
  const { containerRef, captureNaturalSize, display: orientedDisplay } = useOrientedMedia(rotationDeg);

  const beforePicker = useDailyImagePicker(feedId);
  const afterPicker = useDailyImagePicker(feedId);
  const [beforeIsDefault, setBeforeIsDefault] = useState(true);
  const setBeforeFromCheck = useCallback((image) => setBeforeImage({ url: image.url, date: image.date }), []);
  const framing = useFramingCheck({ feedId, before: beforeImage, after: afterImage, setBefore: setBeforeFromCheck, isDefaultBefore: beforeIsDefault });

  const lens = useRef({ cx: 0.5, cy: 0.5, w: 0.32, h: 0.22 });
  const shapeRef = useRef(shape);
  shapeRef.current = shape;
  const beforeWrapRef = useRef(null);
  const frameRef = useRef(null);
  const drawFrame = useRef(0);

  const zoomStateRef = useRef({ s: 1, x: 0, y: 0 });

  const draw = useCallback(() => {
    drawFrame.current = 0;
    const wrap = beforeWrapRef.current;
    const frame = frameRef.current;
    const W = wrap?.parentElement?.offsetWidth;
    const H = wrap?.parentElement?.offsetHeight;
    if (!wrap || !frame || !W || !H) return;
    const z = zoomStateRef.current;
    const L = lens.current;
    const sw = L.w * W;
    const sh = shapeRef.current === 'circle' ? sw : L.h * W;
    const sx = L.cx * W;
    const sy = L.cy * H;
    wrap.style.clipPath = shapeRef.current === 'circle'
      ? `circle(${sw / 2}px at ${sx}px ${sy}px)`
      : `inset(${sy - sh / 2}px ${W - (sx + sw / 2)}px ${H - (sy + sh / 2)}px ${sx - sw / 2}px round 6px)`;
    frame.style.left = `${z.x + z.s * (sx - sw / 2)}px`;
    frame.style.top = `${z.y + z.s * (sy - sh / 2)}px`;
    frame.style.width = `${z.s * sw}px`;
    frame.style.height = `${z.s * sh}px`;
    frame.style.borderRadius = shapeRef.current === 'circle' ? '9999px' : `${Math.max(6, 6 * z.s)}px`;
  }, []);

  const scheduleDraw = useCallback(() => {
    if (!drawFrame.current) drawFrame.current = requestAnimationFrame(draw);
  }, [draw]);

  const clampLens = useCallback(() => {
    const wrap = beforeWrapRef.current;
    const W = wrap?.parentElement?.offsetWidth || 1;
    const H = wrap?.parentElement?.offsetHeight || 1;
    const L = lens.current;
    const z = zoomStateRef.current;
    const minW = MIN_LENS_PX / z.s / W;
    L.w = Math.min(1, Math.max(minW, L.w));
    L.h = Math.min(H / W, Math.max(minW, L.h));
    L.cx = Math.min(1, Math.max(0, L.cx));
    L.cy = Math.min(1, Math.max(0, L.cy));
  }, []);

  const resizeLens = useCallback((factor) => {
    lens.current.w *= factor;
    lens.current.h *= factor;
    clampLens();
    scheduleDraw();
  }, [clampLens, scheduleDraw]);

  const zoom = useSyncedZoom({
    onChange: (z) => {
      zoomStateRef.current = z;
      draw();
    },
    onShiftWheel: (e) => {
      const delta = e.deltaY || e.deltaX;
      resizeLens(delta < 0 ? 1.08 : 1 / 1.08);
    },
  });
  const zoomViewportRef = zoom.viewportRef;
  const setViewport = useCallback((el) => {
    containerRef(el);
    zoomViewportRef(el);
  }, [containerRef, zoomViewportRef]);

  useLayoutEffect(() => {
    draw();
  });

  useEffect(() => {
    const onResize = () => scheduleDraw();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (drawFrame.current) cancelAnimationFrame(drawFrame.current);
    };
  }, [scheduleDraw]);

  useEffect(() => {
    if (!feedId) {
      setIsLoading(false);
      return undefined;
    }
    let isMounted = true;
    setIsLoading(true);
    Promise.allSettled([getFirstImageCandidate(feedId), getLatestImage(feedId), ahoraPrecalculado(feedId)]).then(([firstResult, lastResult, ahoraPre]) => {
      if (!isMounted) return;
      if (firstResult.status === 'fulfilled' && firstResult.value?.url) {
        setBeforeImage({ url: firstResult.value.url, date: firstResult.value.date });
      }
      if (lastResult.status === 'fulfilled') {
        const response = lastResult.value;
        const url = typeof response === 'object' ? response?.url : response;
        const date = typeof response === 'object' ? response?.date : null;
        if (url) setAfterImage({ url, date });
      }
      // Par precalculado: "ahora" = la foto ya verificada contra "antes" (nunca un par desalineado).
      if (ahoraPre.status === 'fulfilled' && ahoraPre.value) setAfterImage(ahoraPre.value);
      setIsLoading(false);
    });
    return () => {
      isMounted = false;
    };
  }, [feedId]);

  useEffect(() => {
    if (!beforeImage || !afterImage) return undefined;
    let isMounted = true;
    let pending = 2;
    setImagesReady(false);
    const done = () => {
      pending -= 1;
      if (pending === 0 && isMounted) setImagesReady(true);
    };
    const a = new Image();
    a.onload = () => { if (isMounted) captureNaturalSize(a); done(); };
    a.onerror = done;
    a.src = afterImage.url;
    const b = new Image();
    b.onload = done;
    b.onerror = done;
    b.src = beforeImage.url;
    return () => {
      isMounted = false;
    };
  }, [beforeImage, afterImage, captureNaturalSize]);

  // Arrastre de la lente (mover) o de la manija (redimensionar), con mouse o dedo.
  const startDrag = (mode) => (e) => {
    if (e.button > 0) return;
    e.preventDefault();
    e.stopPropagation();
    const target = e.currentTarget;
    target.setPointerCapture?.(e.pointerId);
    const wrap = beforeWrapRef.current;
    const W = wrap?.parentElement?.offsetWidth || 1;
    const H = wrap?.parentElement?.offsetHeight || 1;
    const start = { x: e.clientX, y: e.clientY, ...lens.current };
    const onMove = (ev) => {
      const s = zoomStateRef.current.s;
      const dx = (ev.clientX - start.x) / s;
      const dy = (ev.clientY - start.y) / s;
      const L = lens.current;
      if (mode === 'move') {
        L.cx = start.cx + dx / W;
        L.cy = start.cy + dy / H;
      } else {
        // La esquina superior izquierda queda fija; el centro acompaña.
        L.w = start.w + dx / W;
        L.cx = start.cx + dx / W / 2;
        if (shapeRef.current === 'circle') {
          L.cy = start.cy + (dx / W / 2) * (W / H);
        } else {
          L.h = start.h + dy / W;
          L.cy = start.cy + dy / H / 2;
        }
      }
      clampLens();
      scheduleDraw();
    };
    const onUp = () => {
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      target.removeEventListener('pointercancel', onUp);
    };
    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
    target.addEventListener('pointercancel', onUp);
  };

  const onLensKeyDown = (e) => {
    const L = lens.current;
    const step = e.altKey ? 0.005 : 0.02;
    const moves = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
    if (moves[e.key]) {
      const [mx, my] = moves[e.key];
      if (e.shiftKey) {
        L.w *= 1 + mx * 0.08;
        L.h *= 1 - my * 0.08;
        if (!mx && shapeRef.current === 'circle') L.w *= 1 - my * 0.08;
      } else {
        L.cx += mx * step;
        L.cy += my * step;
      }
      clampLens();
      scheduleDraw();
      e.preventDefault();
    } else if (e.key === 'c' || e.key === 'C') {
      setShape((prev) => (prev === 'circle' ? 'rect' : 'circle'));
      e.preventDefault();
    }
  };

  if (!isLoading && (!beforeImage || !afterImage)) {
    return (
      <div className="w-full aspect-video bg-gray-100 rounded-xl border border-dashed border-gray-300 flex items-center justify-center text-center p-4">
        <p className="text-sm text-gray-600">Todavía no hay fotos suficientes de esta cámara para usar Rayos X.</p>
      </div>
    );
  }

  if (!isLoading && imagesReady && framing.checking) {
    return <FramingLoading searching={framing.searching} progress={framing.progress} />;
  }

  if (isLoading || !imagesReady) {
    return (
      <div className="w-full aspect-video bg-gray-300 rounded-lg relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="w-10 h-10 animate-spin text-gray-500" />
        </div>
      </div>
    );
  }

  const fallbackImgStyle = { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' };
  const imgStyle = orientedDisplay?.style || fallbackImgStyle;
  const beforeShort = formatShort(beforeImage.date);

  return (
    <div>
      <div
        ref={setViewport}
        tabIndex={0}
        aria-label="Rayos X: foto de ahora con una lente que muestra la foto de antes. Rueda del mouse o pellizco para hacer zoom."
        className={`relative w-full aspect-video rounded-xl overflow-hidden select-none shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${orientedDisplay?.isVertical ? 'bg-black' : 'bg-gray-200'}`}
      >
        <div ref={zoom.stageRef} className="absolute inset-0" style={{ transformOrigin: '0 0' }}>
          <img src={afterImage.url} alt="Foto de ahora" style={imgStyle} draggable={false} />
          <div ref={beforeWrapRef} className="absolute inset-0" style={{ opacity: opacity / 100 }}>
            <img src={beforeImage.url} alt="Foto de antes" style={{ ...imgStyle, transform: alignmentTransform(framing.alignment, imgStyle.transform) }} draggable={false} />
          </div>
        </div>

        <div
          ref={frameRef}
          data-zoom-ignore
          role="group"
          tabIndex={0}
          aria-label={`Lente de antes${beforeShort ? ` (${beforeShort})` : ''}. Flechas para mover, Shift más flechas para cambiar el tamaño, C para cambiar la forma.`}
          onPointerDown={startDrag('move')}
          onKeyDown={onLensKeyDown}
          className="absolute z-10 cursor-move border-2 border-white ring-1 ring-black/20 shadow-[0_0_0_9999px_rgba(0,0,0,0.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          style={{ touchAction: 'none' }}
        >
          <span
            className={`absolute left-1/2 -translate-x-1/2 whitespace-nowrap bg-white/95 text-gray-800 text-[11px] font-semibold px-2 py-0.5 rounded-full shadow-sm pointer-events-none ${shape === 'circle' ? 'top-2' : 'top-1.5'}`}
          >
            Antes{beforeShort ? ` · ${beforeShort}` : ''}
          </span>
          <span
            onPointerDown={startDrag('resize')}
            aria-hidden="true"
            title="Arrastrá para cambiar el tamaño"
            className={`absolute w-5 h-5 bg-white border-2 border-blue-600 rounded-full shadow cursor-nwse-resize ${shape === 'circle' ? 'right-[14.6%] bottom-[14.6%] translate-x-1/2 translate-y-1/2' : '-right-2.5 -bottom-2.5'}`}
            style={{ touchAction: 'none' }}
          />
        </div>

        <div className="absolute top-2 right-2 pointer-events-none bg-black/60 text-white text-xs px-2 py-1 rounded">
          Ahora{afterImage.date ? ` · ${formatDateEs(afterImage.date)}` : ''}
        </div>
        <ZoomIndicator ref={zoom.indicatorRef} onReset={() => zoom.reset(true)} />
      </div>

      <FramingNotice framing={framing} />

      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <button
            type="button"
            onClick={() => beforePicker.open(parseDateSafe(beforeImage.date) || new Date())}
            className="flex items-center gap-1 text-sm text-blue-600 hover:underline font-medium"
          >
            <CalendarIcon className="w-4 h-4" />
            Antes: {formatDateEs(beforeImage.date) || 'elegir'}
          </button>
          <button
            type="button"
            onClick={() => afterPicker.open(parseDateSafe(afterImage.date) || new Date())}
            className="flex items-center gap-1 text-sm text-blue-600 hover:underline font-medium"
          >
            <CalendarIcon className="w-4 h-4" />
            Ahora: {formatDateEs(afterImage.date) || 'elegir'}
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5" role="radiogroup" aria-label="Forma de la lente">
            {[{ id: 'rect', label: 'Rectángulo', Icon: Square }, { id: 'circle', label: 'Círculo', Icon: Circle }].map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={shape === id}
                onClick={() => setShape(id)}
                className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${shape === id ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-600">
            Opacidad
            <input
              type="range"
              min={20}
              max={100}
              step={5}
              value={opacity}
              onChange={(e) => setOpacity(Number(e.target.value))}
              className="w-24 accent-blue-600"
              aria-label="Opacidad de la foto de antes dentro de la lente"
            />
          </label>
        </div>
      </div>
      <p className="mt-2 text-xs text-gray-500">
        Arrastrá la lente para ver cómo estaba ese lugar.
        <span className="hidden sm:inline"> Cambiá el tamaño con la manija o con Shift + rueda. Rueda para hacer zoom, doble clic para volver.</span>
        <span className="sm:hidden"> Cambiá el tamaño con la manija. Pellizcá para hacer zoom, doble toque para volver.</span>
      </p>

      {beforePicker.isOpen && (
        <DatePickerModal
          title="Rayos X · Elegir la foto de antes"
          user={user}
          picker={beforePicker}
          onSelectImage={(image) => { framing.userPicked(); setBeforeIsDefault(false); setBeforeImage({ url: image.url, date: image.date }); beforePicker.close(); }}
          onClose={beforePicker.close}
        />
      )}
      {afterPicker.isOpen && (
        <DatePickerModal
          title="Rayos X · Elegir la foto de ahora"
          user={user}
          picker={afterPicker}
          onSelectImage={(image) => { framing.userPicked(); setAfterImage({ url: image.url, date: image.date }); afterPicker.close(); }}
          onClose={afterPicker.close}
        />
      )}
    </div>
  );
};

export default RayosXCompare;
